// src/components/Languages/ViewEditLanguageModal.js
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveLanguageContent, fetchLanguages, downloadLanguageFile } from "../../redux/slices/languagesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { XMarkIcon, PencilIcon, EyeIcon, PlusIcon, TrashIcon, MagnifyingGlassIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/LanguageContext";

const ViewEditLanguageModal = ({ isOpen, setIsOpen, language }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { saving, cachedContent } = useSelector((state) => state.languages);

  const [keyValuePairs, setKeyValuePairs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [keyError, setKeyError] = useState("");
  const [usingCache, setUsingCache] = useState(false);

  // Load content from Redux cache or fetch from URL
  const loadContent = useCallback(async (languageKey, url) => {
    setIsFetching(true);
    setError("");

    // First, try to use cached content from Redux store (loaded from local files or server)
    const cached = cachedContent[languageKey];
    if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
      const pairs = Object.entries(cached).map(([key, value]) => ({ key, value }));
      setKeyValuePairs(pairs);
      setUsingCache(true);
      setIsFetching(false);
      return;
    }

    // If not cached, fetch directly from the URL (CloudFront)
    setUsingCache(false);
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const pairs = Object.entries(data).map(([key, value]) => ({ key, value }));
        setKeyValuePairs(pairs);
      } else {
        setError(t("language.viewEdit.invalidFormat"));
      }
    } catch (err) {
      console.error("Failed to fetch JSON from URL:", err);
      setError(t("language.viewEdit.loadFailed"));
    } finally {
      setIsFetching(false);
    }
  }, [cachedContent, t]);

  useEffect(() => {
    if (isOpen && language?.key) {
      loadContent(language.key, language.url);
    }
  }, [isOpen, language, loadContent]);

  // Refresh content from server (re-download)
  const handleRefreshFromServer = async () => {
    if (!language?.key) return;
    setIsFetching(true);
    setError("");
    setUsingCache(false);

    try {
      // Download and get the result directly from the thunk
      const result = await dispatch(downloadLanguageFile(language)).unwrap();
      // Use the content from the result, not from state (state hasn't updated yet)
      if (result && result.content) {
        const pairs = Object.entries(result.content).map(([key, value]) => ({ key, value }));
        setKeyValuePairs(pairs);
        setUsingCache(true);
        dispatch(showNotification({ message: t("language.viewEdit.refreshedSuccess"), type: "success" }));
      } else {
        setError(t("language.viewEdit.noContent"));
      }
    } catch (err) {
      console.error("Refresh error:", err);
      setError(t("language.viewEdit.refreshFailed"));
      dispatch(showNotification({ message: t("language.viewEdit.refreshFailed"), type: "error" }));
    } finally {
      setIsFetching(false);
    }
  };

  const handleValueChange = (index, newValue) => {
    const updated = [...keyValuePairs];
    updated[index].value = newValue;
    setKeyValuePairs(updated);
  };

  const handleKeyChange = (index, newKey) => {
    const updated = [...keyValuePairs];
    updated[index].key = newKey;
    setKeyValuePairs(updated);
  };

  const handleDeletePair = (index) => {
    const updated = keyValuePairs.filter((_, i) => i !== index);
    setKeyValuePairs(updated);
  };

  const handleAddNewPair = () => {
    if (!newKey.trim()) {
      setKeyError(t("language.viewEdit.keyRequired"));
      return;
    }
    // Check for duplicate key
    if (keyValuePairs.some(pair => pair.key === newKey.trim())) {
      setKeyError(t("language.viewEdit.keyDuplicate"));
      return;
    }

    setKeyValuePairs([...keyValuePairs, { key: newKey.trim(), value: newValue }]);
    setNewKey("");
    setNewValue("");
    setShowAddForm(false);
    setKeyError("");
  };

  const handleSave = async () => {
    // Convert key-value pairs back to JSON object
    const jsonObject = {};
    for (const pair of keyValuePairs) {
      if (pair.key.trim()) {
        jsonObject[pair.key] = pair.value;
      }
    }

    try {
      // Use the saveLanguageContent thunk which updates both server and local cache
      await dispatch(saveLanguageContent({ key: language.key, content: jsonObject })).unwrap();
      dispatch(showNotification({ message: t("language.viewEdit.savedSuccess"), type: "success" }));
      dispatch(fetchLanguages({ currentPage: 1, limit: 10 }));
      setIsEditing(false);
      handleClose();
    } catch (error) {
      dispatch(showNotification({ message: error || t("language.viewEdit.saveFailed"), type: "error" }));
    }
  };

  const handleClose = () => {
    setKeyValuePairs([]);
    setIsEditing(false);
    setError("");
    setSearchTerm("");
    setNewKey("");
    setNewValue("");
    setShowAddForm(false);
    setKeyError("");
    setUsingCache(false);
    setIsOpen(false);
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    setShowAddForm(false);
    setNewKey("");
    setNewValue("");
    setKeyError("");
  };

  // Filter key-value pairs based on search term
  const filteredPairs = keyValuePairs.filter(pair =>
    pair.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pair.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen || !language) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-dashboardDark">
              {language.name} ({language.key})
            </h2>
            <span className={`px-2 py-1 text-xs rounded-full ${isEditing ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
              {isEditing ? t("language.viewEdit.editing") : t("language.viewEdit.viewing")}
            </span>
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
              {keyValuePairs.length} {t("language.viewEdit.keys")}
            </span>
            {usingCache && (
              <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                {t("language.viewEdit.localCache")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Refresh from Server Button */}
            <button
              onClick={handleRefreshFromServer}
              disabled={isFetching}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              title={t("language.viewEdit.refreshTooltip")}
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              {t("language.viewEdit.refresh")}
            </button>
            <button
              onClick={toggleEditMode}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                isEditing
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
            >
              {isEditing ? (
                <>
                  <EyeIcon className="w-4 h-4" />
                  {t("language.viewEdit.viewMode")}
                </>
              ) : (
                <>
                  <PencilIcon className="w-4 h-4" />
                  {t("language.viewEdit.editMode")}
                </>
              )}
            </button>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t("language.viewEdit.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dashboardCoral"
            />
          </div>
          {isEditing && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-dashboardCoral text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              {t("language.viewEdit.addNewKey")}
            </button>
          )}
        </div>

        {/* Add New Key Form */}
        {showAddForm && (
          <div className="px-6 py-4 bg-green-50 border-b border-green-200">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("language.viewEdit.keyLabel")}</label>
                <input
                  type="text"
                  value={newKey}
                  onChange={(e) => {
                    setNewKey(e.target.value);
                    setKeyError("");
                  }}
                  placeholder={t("language.viewEdit.keyPlaceholder")}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dashboardCoral ${
                    keyError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {keyError && <p className="text-red-500 text-sm mt-1">{keyError}</p>}
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("language.viewEdit.valueLabel")}</label>
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder={t("language.viewEdit.valuePlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dashboardCoral"
                />
              </div>
              <div className="flex items-end gap-2 pt-6">
                <button
                  onClick={handleAddNewPair}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {t("language.viewEdit.add")}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewKey("");
                    setNewValue("");
                    setKeyError("");
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t("language.viewEdit.cancel")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isFetching ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dashboardCoral"></div>
              <span className="ml-2">{t("language.viewEdit.loading")}</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <p>{error}</p>
              <button
                onClick={() => language?.key && loadContent(language.key, language.url)}
                className="mt-4 px-4 py-2 bg-dashboardCoral text-white rounded-lg hover:bg-opacity-90"
              >
                {t("language.viewEdit.retry")}
              </button>
            </div>
          ) : (
            <div className="h-[50vh] overflow-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 w-1/3">{t("language.viewEdit.keyLabel")}</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">{t("language.viewEdit.valueLabel")}</th>
                    {isEditing && (
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700 w-20">{t("language.viewEdit.actions")}</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPairs.length === 0 ? (
                    <tr>
                      <td colSpan={isEditing ? 3 : 2} className="px-6 py-8 text-center text-gray-500">
                        {searchTerm ? t("language.viewEdit.noMatchingKeys") : t("language.viewEdit.noTranslations")}
                      </td>
                    </tr>
                  ) : (
                    filteredPairs.map((pair, index) => {
                      const originalIndex = keyValuePairs.findIndex(p => p.key === pair.key);
                      return (
                        <tr key={pair.key} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            {isEditing ? (
                              <input
                                type="text"
                                value={pair.key}
                                onChange={(e) => handleKeyChange(originalIndex, e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dashboardCoral font-mono text-sm"
                              />
                            ) : (
                              <code className="text-sm bg-gray-100 px-2 py-1 rounded text-blue-700">
                                {pair.key}
                              </code>
                            )}
                          </td>
                          <td className="px-6 py-3">
                            {isEditing ? (
                              <input
                                type="text"
                                value={pair.value}
                                onChange={(e) => handleValueChange(originalIndex, e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dashboardCoral text-sm"
                                dir="auto"
                              />
                            ) : (
                              <span className="text-sm text-gray-700" dir="auto">
                                {pair.value}
                              </span>
                            )}
                          </td>
                          {isEditing && (
                            <td className="px-6 py-3 text-center">
                              <button
                                onClick={() => handleDeletePair(originalIndex)}
                                className="p-1 hover:bg-red-100 rounded transition-colors text-red-500"
                                title={t("language.viewEdit.delete")}
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-3 px-6 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {searchTerm && t("language.viewEdit.showingCount").replace("{filtered}", filteredPairs.length).replace("{total}", keyValuePairs.length)}
            {!searchTerm && usingCache && (
              <span className="text-green-600">{t("language.viewEdit.loadedFromCache")}</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {isEditing ? t("language.viewEdit.cancel") : t("language.viewEdit.close")}
            </button>
            {isEditing && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-dashboardCoral text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
              >
                {saving ? t("language.viewEdit.saving") : t("language.viewEdit.save")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewEditLanguageModal;
